"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useSession, signOut } from "next-auth/react";
import { useRouter } from "next/navigation";
import apiClient from "@/libs/api";
import { trackFunnelEvent } from "@/libs/ga";
import { REDIRECT_MESSAGES, showRedirectLoading } from "@/libs/redirectLoading";

/**
 * Anonymous / unauthenticated-visitor state.
 *
 * Free-tier users don't have a User document, so `credits` below is derived
 * from the IP-rate-limit counter on the server. Tool pages treat this as
 * "virtual credits" - each tool invocation costs 1, refilled on a 24h
 * rolling window.
 */
export interface FreeTierState {
  used: number;
  remaining: number;
  limit: number;
  resetsAt: string | null;
}

interface CreditsContextType {
  /** Current balance. For authed users this is the real ledger balance.
   *  For anon users it is `freeTier.remaining`. */
  credits: number | null;
  // Derived from subscription + purchase history on the server (1 = free, 2 = paid)
  uqf: number | null;
  // Stripe subscription status ("active", "canceled", "past_due", "unpaid", "none")
  subscriptionStatus: string | null;
  isLoading: boolean;
  /** True when the visitor is not signed in - UI should render free-tier
   *  affordances (x/5 badge, "sign up to continue" CTA). */
  isAnonymous: boolean;
  /** IP-based free-tier counter. `null` for authenticated users. */
  freeTier: FreeTierState | null;
  /** Fast client-side check backed by `/api/credits/check` (authed) or the
   *  cached free-tier counter (anon). Purely informational - never the
   *  source of truth for billing. */
  checkCredits: (amount: number) => Promise<boolean>;
  /** Re-fetch the cached balance + subscription state (authed) or the
   *  free-tier counter (anon). Call after any tool invocation so the UI
   *  reflects the new balance without waiting for a natural refresh. */
  refreshCredits: () => Promise<void>;
  error: string | null;
}

const CreditsContext = createContext<CreditsContextType | undefined>(undefined);

export function CreditsProvider({ children }: { children: ReactNode }) {
  const { data: session, status: sessionStatus } = useSession();
  const router = useRouter();
  const [credits, setCredits] = useState<number | null>(null);
  const [uqf, setUqf] = useState<number | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [freeTier, setFreeTier] = useState<FreeTierState | null>(null);

  const isAnonymous = sessionStatus === "unauthenticated";

  // Fetch user credits + subscription status from /api/user/me.
  // isInitialLoad=false performs a background refresh without flipping the loading spinner.
  const fetchUserCredits = async (isInitialLoad: boolean = true) => {
    if (!session?.user?.id) {
      setIsLoading(false);
      return;
    }

    try {
      if (isInitialLoad) {
        setIsLoading(true);
      }
      const response = await apiClient.get("/user/me");
      const data = response as unknown as {
        credits: number;
        uqf: number;
        subscription?: { status?: string | null };
      };
      setCredits(data?.credits ?? 0);
      setUqf(data?.uqf ?? 1);
      setSubscriptionStatus(data?.subscription?.status ?? null);
    } catch (err: any) {
      console.error("Error fetching user credits:", err);

      // Session is still valid but the user record was deleted - sign them out.
      if (err?.response?.status === 404) {
        showRedirectLoading(REDIRECT_MESSAGES.signingOut);
        await signOut({ redirect: false });
        router.push("/");
      } else {
        setError(err?.message || "Failed to fetch credits");
      }
    } finally {
      if (isInitialLoad) {
        setIsLoading(false);
      }
    }
  };

  // Anonymous free-tier: read the IP-rate-limit counter. Safe to call without
  // a session; no PII is exchanged, just { used, remaining, limit, resetsAt }.
  const fetchFreeTier = async () => {
    try {
      const res = await fetch("/api/free-usage", {
        method: "GET",
        cache: "no-store",
        credentials: "same-origin",
      });
      if (!res.ok) throw new Error(`free-usage ${res.status}`);
      const data = (await res.json()) as FreeTierState;
      setFreeTier(data);
      setCredits(data.remaining);
    } catch (err: any) {
      // Non-fatal - UI can still render a neutral "free tier" badge.
      console.error("Error fetching free-tier state:", err);
      setFreeTier({
        used: 0,
        remaining: 0,
        limit: 10,
        resetsAt: null,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const refreshCredits = async () => {
    if (isAnonymous) {
      await fetchFreeTier();
      return;
    }
    await fetchUserCredits(false);
  };

  const checkCredits = async (amount: number): Promise<boolean> => {
    setError(null);

    // Anonymous users: rely on the cached free-tier counter. The real
    // enforcement happens server-side inside each tool route (atomic).
    if (isAnonymous) {
      const remaining = freeTier?.remaining ?? 0;
      const hasEnough = remaining >= amount;
      if (!hasEnough) {
        trackFunnelEvent("monetization", "free_tier_exhausted", {
          credits_needed: amount,
          remaining,
        });
      }
      return hasEnough;
    }

    try {
      const response = await apiClient.post("/credits/check", { amount });
      const data = response as unknown as { hasCredits: boolean };
      const hasEnough = data?.hasCredits === true;

      if (!hasEnough) {
        trackFunnelEvent("monetization", "insufficient_credits", {
          credits_needed: amount,
          user_credits: credits,
          deficit: amount - (credits || 0),
        });
      }

      return hasEnough;
    } catch (err: any) {
      setError(err?.message || "Error checking credits");
      return false;
    }
  };

  useEffect(() => {
    if (sessionStatus === "loading") {
      setIsLoading(true);
      return;
    }

    if (sessionStatus === "unauthenticated") {
      // Switch context into free-tier mode: the "balance" now comes from
      // the per-IP counter at /api/free-usage instead of the User ledger.
      setUqf(null);
      setSubscriptionStatus(null);
      fetchFreeTier();
      return;
    }

    if (session?.user?.id) {
      // Leaving free-tier mode - drop the anon counter so a later sign-out
      // doesn't show stale data.
      setFreeTier(null);
      // IMPORTANT: Always fetch user credits when authenticated, even if we have
      // a value from free-tier. The previous free-tier credits should NOT carry
      // over to the authenticated session.
      fetchUserCredits();
    } else {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id, sessionStatus]);

  const value: CreditsContextType = {
    credits,
    uqf,
    subscriptionStatus,
    isLoading,
    isAnonymous,
    freeTier,
    error,
    checkCredits,
    refreshCredits,
  };

  return (
    <CreditsContext.Provider value={value}>{children}</CreditsContext.Provider>
  );
}

export function useCreditsContext() {
  const context = useContext(CreditsContext);
  if (context === undefined) {
    throw new Error("useCreditsContext must be used within a CreditsProvider");
  }
  return context;
}
